/**
 * Bandeau de KPIs — 4 tuiles (`StatTile`), même lecture que la maquette :
 * passations terminées, diplômés (≥ seuil), créneaux non couverts, temps
 * médian par test.
 *
 * Les tuiles sont celles de la librairie (`@dozka/ui`) : le ton de sens est celui
 * de la tuile (`tone`) — vert pour ceux qui attrapent les pièges, rouge quand un
 * créneau n'est couvert par personne —, jamais une couleur écrite à la main.
 * Une absence d'info reste « — », jamais « 0 ».
 */
import { Grille, StatTile } from "@/shared/ui";

import { GRADUATE_THRESHOLD } from "../constants";
import { countCoverageGaps, formatDwellMinutes, median } from "../format";
import type { CandidateListItem, CoverageMatrix } from "../types";

interface AdminKpisProps {
  candidates: CandidateListItem[];
  coverage: CoverageMatrix | null;
}

export function AdminKpis({ candidates, coverage }: AdminKpisProps) {
  const submitted = candidates.filter((c) => c.status === "submitted");
  const graduates = submitted.filter(
    (c) => (c.score?.fault_catch_rate ?? 0) >= GRADUATE_THRESHOLD,
  ).length;
  const gaps = coverage ? countCoverageGaps(coverage) : null;
  const dwellValues = submitted
    .map((c) => c.score?.dwell_median_ms)
    .filter((n): n is number => typeof n === "number");
  const medianDwell = median(dwellValues);

  return (
    <Grille min={260}>
      <StatTile
        label="Passations terminées"
        value={`${submitted.length} / ${candidates.length}`}
        title="Passations soumises sur liens envoyés"
      />
      <StatTile
        label={`Attrapent les pièges (≥ ${Math.round(GRADUATE_THRESHOLD * 100)} %)`}
        value={String(graduates)}
        tone="valide"
      />
      <StatTile
        label="Créneaux non couverts"
        value={gaps === null ? "—" : String(gaps)}
        tone={gaps ? "erreur" : "marque"}
      />
      <StatTile
        label="Temps médian / test"
        value={medianDwell === null ? "—" : formatDwellMinutes(medianDwell)}
      />
    </Grille>
  );
}
